import { useCallback } from 'react'
import {
  HOOD_MAP,
  SEARCH_PAGE_CITY_MAP,
  SearchPageCity
} from 'src/constants/searchPages'
import useCommon from 'src/hooks/useCommon'
import useTrackEvent from 'src/hooks/useTrackEvent'
import {
  IConfiguration,
  ILocation,
  IUser,
  Maybe,
  StorageTime
} from 'src/types/graphql'
import { getAnalyticsLocationType } from 'src/utils/pixels/enrichment'
import fbp from './fbp'
import ga from './gtag'
import { getHsq } from './hubspot'
import { isInternalTraffic } from './internal'
import posthog from './posthog'
import { addToCart, generateLead, GenerateLeadParams } from './spotmeEvents'
import ms from './uetq'

/** Normalize geo strings for consistent analytics (lowercase, trimmed, accent-stripped) */
export const norm = (value: string | null | undefined): string | undefined =>
  value
    ?.trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\s-]+/g, ' ') || undefined

// ---------------------------------------------------------------------------
// Canonical geo lookups — maps raw address strings to slug-format values
// so analytics events always group consistently (e.g. "Ciudad de México",
// "CDMX", "ciudad de mexico" all become "ciudad-de-mexico").
// ---------------------------------------------------------------------------

/** Convert a raw string to a slug (lowercase, accent-stripped, spaces → hyphens). */
const slugify = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\s]+/g, '-')

/**
 * Build a reverse lookup: norm(displayName) → slug.
 * Includes cityName, cityNameShort, and manual aliases from the backend.
 */
const buildCityLookup = (): Map<string, string> => {
  const map = new Map<string, string>()
  for (const entry of Object.values(SEARCH_PAGE_CITY_MAP)) {
    if (!entry.city) continue
    // Map slugified cityName → slug (e.g. "monterrey" → "monterrey")
    map.set(slugify(entry.cityName), entry.city)
    if (entry.cityNameShort) {
      map.set(slugify(entry.cityNameShort), entry.city)
    }
  }
  // Manual aliases (mirrors backend ADDRESS_CITY_ALIASES)
  map.set('cdmx', SearchPageCity.CiudadDeMexico)
  map.set('df', SearchPageCity.CiudadDeMexico)
  map.set('mexico-city', SearchPageCity.CiudadDeMexico)
  map.set('san-pedro', SearchPageCity.SanPedro)
  map.set('spgg', SearchPageCity.SanPedro)
  map.set('escobedo', SearchPageCity.Escobedo)
  map.set('san-nicolas-de-los-garza', SearchPageCity.SanNicolas)
  map.set('santiago-de-queretaro', SearchPageCity.Queretaro)
  map.set('cholula', SearchPageCity.SanAndresCholula)
  map.set('rosarito', SearchPageCity.PlayasDeRosarito)
  map.set('marques', SearchPageCity.ElMarques)
  map.set('tlaquepaque', SearchPageCity.Tlaquepaque)
  map.set('tlajomulco', SearchPageCity.Tlajomulco)
  map.set('tonala', SearchPageCity.Tonala)
  map.set('queretaro', SearchPageCity.Queretaro)
  map.set('soledad', SearchPageCity.Soledad)
  map.set('gomez-palacio', SearchPageCity.GomezPalacio)
  map.set('ciudad-madero', SearchPageCity.CiudadMadero)
  return map
}

const buildHoodLookup = (): Map<string, string> => {
  const map = new Map<string, string>()
  for (const [slug, entry] of Object.entries(HOOD_MAP)) {
    map.set(slugify(entry.hoodName), slug)
  }
  return map
}

const CITY_LOOKUP = buildCityLookup()
const HOOD_LOOKUP = buildHoodLookup()

/** Map a raw city string to its canonical slug, falling back to slugified form. */
export const canonicalCity = (
  value: string | null | undefined
): string | undefined => {
  if (!value) return undefined
  const slug = slugify(value)
  return CITY_LOOKUP.get(slug) ?? slug
}

/** Map a raw neighborhood string to its canonical slug, falling back to slugified form. */
export const canonicalHood = (
  value: string | null | undefined
): string | undefined => {
  if (!value) return undefined
  const slug = slugify(value)
  return HOOD_LOOKUP.get(slug) ?? slug
}

const useAddToCart = () => {
  const { configLocMap } = useCommon()
  const { trackEvent, EventAction } = useTrackEvent()

  const handleAddToCart = useCallback(
    (
      event:
        | 'broker_contact'
        | 'traditional_contact'
        | 'begin_checkout'
        | 'contact_wa'
        | 'chat_host'
        | 'technical_report',
      location: ILocation,
      config: Maybe<IConfiguration> | undefined,
      quantity: number = 1,
      storageTime?: StorageTime | '',
      /**
       * When the caller knows downstream code (e.g. `<LeadForm>`'s Step 1
       * useEffect) will fire the canonical `addToCart` pixel for the same
       * intent, pass `{ skipPixelFanout: true }` to avoid double-firing
       * GA4/Meta/UET/LinkedIn. The internal ES `trackEvent` + Clarity
       * still fire from this call (LeadForm doesn't dispatch those), so
       * the marketing-report split by subType stays accurate.
       *
       * Used by Broker/Traditional/Fleet CTA handlers that open the
       * unified LeadForm. Monthly's `begin_checkout`, the WA FAB's
       * `contact_wa`, and User.tsx's `chat_host` paths do NOT use this
       * — they're terminal CTAs (no LeadForm Step 1 mount downstream),
       * so they own the pixel fanout themselves.
       */
      options?: { skipPixelFanout?: boolean }
    ) => {
      const locType = configLocMap(config)

      trackEvent({
        action: EventAction.AddToCart,
        locationId: location.id,
        configurationId: config?.id,
        // Forward the subtype so the marketing report can split funnels:
        //   begin_checkout → SpotMe Now | broker_contact → Pro | traditional_contact → Traditional
        subType: event
      })

      // PostHog — tag this person/session as a high-intent add_to_cart
      // event with the listing context. Replaces the prior Clarity
      // `upgrade` + `set` calls (Clarity was removed 2026-05-13).
      //
      // `location_type` is the lowercase enum value (canonical analytics
      // shape) — never the Spanish display label from `configLocMap`,
      // which is locale-dependent and would emit different values per
      // user for the same listing.
      posthog.event('add_to_cart_intent', {
        location_id: location.id,
        configuration_id: config?.id,
        sub_event: event,
        location_type: getAnalyticsLocationType(config?.locationType)
      })

      // Canonical event — fans out to GA4, Meta, UET, LinkedIn. Skipped
      // when the caller's downstream UI (LeadForm Step 1) will fire it.
      if (!options?.skipPixelFanout) {
        addToCart({
          subEvent: event,
          location,
          config,
          spaceType: locType,
          quantity,
          storageTime
        })
      }
    },
    [configLocMap, trackEvent, EventAction]
  )

  return handleAddToCart
}

/**
 * Hook wrapper around `generateLead` that ALSO writes `LEAD_SUBMIT` to
 * internal ES (subType=method) so the marketing report's funnel tabs
 * attribute Flotillas hero / /reservar phone submit / fleet pill /
 * listing LeadForm completion. Pure `generateLead` only reaches GA4 /
 * Meta / Bing / LinkedIn / PostHog — without this ES write, those leads
 * disappear from the backend report.
 *
 * Use this hook anywhere a demand-side surface fires a lead. Supply
 * pages MUST continue to use `useSupplyLead` instead — they're a
 * separate funnel.
 */
const useGenerateLead = () => {
  const { trackEvent, EventAction } = useTrackEvent()

  return useCallback(
    (params: GenerateLeadParams, listingContext?: ILocationContext) => {
      generateLead(params)
      trackEvent({
        action: EventAction.LeadSubmit,
        subType: params.method,
        locationId: listingContext?.locationId,
        configurationId: listingContext?.configurationId,
        pillar: listingContext?.pillar,
        pagePath:
          typeof window !== 'undefined' ? window.location.pathname : undefined
      })
    },
    [trackEvent, EventAction]
  )
}

/**
 * Optional listing-level context for `useGenerateLead` — passed by callers
 * that fire generateLead from a listing surface (LeadForm submit on a
 * Broker/Traditional listing) so the ES row carries the listing IDs.
 * Landing-page surfaces (Flotillas hero, /tradicional, /anfitrion/*)
 * omit it — subType=method is enough to attribute.
 */
type ILocationContext = {
  locationId?: string
  configurationId?: string
  pillar?: string
}

type IFbUser = {
  id?: string | undefined | null
  email?: string | undefined | null
  phoneNumber?: string | undefined | null
  displayName?: string | undefined | null
}

type IFormUser = {
  name: string
  lastname?: string
  email: string
  phone: string
  additionalInfo: string | null
}

export const identifyUser = (
  currentUser: Maybe<IUser>,
  fbUser: IFbUser | null,
  formUser: IFormUser | null
) => {
  if (isInternalTraffic()) return
  const { userChanges, address } = currentUser ?? {}
  const id = currentUser?.id ?? fbUser?.id
  const email =
    currentUser?.email ?? userChanges?.email ?? fbUser?.email ?? formUser?.email
  const phone =
    currentUser?.phoneNumber ??
    userChanges?.phoneNumber ??
    fbUser?.phoneNumber ??
    formUser?.phone
  const fName =
    currentUser?.firstname ??
    userChanges?.firstname ??
    fbUser?.displayName ??
    formUser?.name
  const lName =
    currentUser?.lastname ?? userChanges?.lastname ?? formUser?.lastname

  // HubSpot
  const _hsq = getHsq()
  if (_hsq && email) _hsq.push(['identify', { email }])

  // Microsoft Bing UET
  ms.getUetq().push('set', { pid: { em: email, ph: phone } })

  // PostHog — identify the user with email/phone/name + SpotMe person
  // properties (customerType, hubspot link, KYC state). Replaces the
  // prior Clarity `identify` call (removed 2026-05-13). PostHog's
  // `person_profiles: 'identified_only'` means this call is what causes
  // a person row to be created and billed against the plan.
  //
  // The rich props unlock cohorts/funnels GA4 can't model:
  //   - customer_type → split RENT_INDIVIDUAL vs RENT_BROKER funnels
  //   - hubspot_id → cross-reference HubSpot deals from PostHog
  //   - hs_owner_id → group leads by sales rep
  //   - is_verified → KYC-completed vs not retention curves
  //   - is_admin → person-property mirror of internal-user tagging
  //
  // Team-group analytics (`posthog.setGroup('team', teamId)`) is a
  // follow-up: the User GraphQL type doesn't expose a team id directly
  // (teams are owners of listings, not a User scalar), so this should
  // be called from team-context pages where a teamId is in scope.
  posthog.identifyUser({
    id,
    email,
    phone,
    fName,
    lName,
    customerType: currentUser?.customerType ?? null,
    hubspotId: currentUser?.hubspotId ?? null,
    hsOwnerId: currentUser?.hsOwnerId ?? null,
    isVerified: currentUser?.isVerified ?? null,
    isAdmin: currentUser?.isAdmin ?? null
  })

  // Google Analytics
  const gtag = ga.getGtag()
  gtag('set', 'user_data', {
    email,
    phone_number: phone,
    address: address
      ? {
          first_name: fName,
          last_name: lName,
          street: address?.street1,
          city: canonicalCity(address?.city),
          region: norm(address?.state),
          postal_code: address?.zipcode,
          country: address?.country
        }
      : undefined
  })
  const dataLayer = ga.getDataLayer()
  dataLayer.push({ user_id: id, email })

  // Facebook Pixel Advanced Matching
  fbp.identifyUser({
    email,
    phone,
    fName,
    lName,
    address: address
      ? {
          city: canonicalCity(address.city) || undefined,
          state: norm(address.state) || undefined,
          zipcode: address.zipcode || undefined,
          country: address.country || undefined
        }
      : undefined
  })
}

export { useGenerateLead }
export default useAddToCart
